<template>
  <AppDialog
    title="Schedule report"
    append-to-body
    :show="show"
    class="schedule-report-dialog"
    @close="onClose"
  >
    <template
      v-if="showFiltersCount"
      #header
    >
      <span
        id="scheduledReport_filter_count"
        class="label label-rounded label-outline label-md active-filters-label"
      >
        There {{ $filters.pluralize(filterCount, 'is', 'are') }} {{ filterCount }} active
        {{ $filters.pluralize(filterCount, 'filter', 'filters') }}
      </span>
    </template>

    <!-- SGT plans banner -->
    <div
      v-if="!deliveryEnabled && limitReached && hubstaffSgt"
      class="form-group well inactive-report-banner"
    >
      <div class="icon"><i class="hi hi-warning" /></div>
      <div class="text">
        This report is inactive because you’ve reached your limit of 3&nbsp;scheduled reports for your current plan.
      </div>
      <div class="link"><a :href="upgradePlanLink">Upgrade plan</a></div>
    </div>

    <div
      v-if="scheduledReportsImprovements"
      class="data-filters mb-10"
    >
      <label class="control-label">
        Data filters
        <i
          v-tooltip="{ placement: 'right' }"
          title="Use filters to limit or modify the data and generate customized reports"
          class="hi hi-info ml-5"
        />
      </label>
      <div v-if="showFiltersDetailed">
        <AppSelect
          :value="filtersDetailed"
          :options="filtersDetailed"
          :select2-options="
            Object.freeze({
              width: '100%',
              multiple: true,
              allowClear: false,
            })
          "
          class="mb-5"
          disabled
        />
        <div class="hint hint-small">Note: Filters can be modified from the filters settings</div>
      </div>
      <div v-else>
        No filters applied. To add filters view
        <a
          href="#"
          @click.prevent="onClose('openFilters')"
        >
          filters
        </a>
        on the report page.
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12">
        <div
          class="form-group"
          :class="{ 'has-error': hasErrorsFor('recipients') }"
        >
          <div>
            <label
              class="control-label"
              for="scheduledReport_recipients"
            >
              Email addresses*
            </label>
          </div>
          <div class="hint hint-small">Separate email addresses with commas</div>
          <AppInput
            id="scheduledReport_recipients"
            v-model="recipients"
          />
          <div
            v-for="error in errorsFor('recipients')"
            :key="error"
            class="help-block"
          >
            {{ error }}
          </div>
        </div>
        <div class="form-group">
          <label class="control-label">Subject</label>
          <p>{{ subject }}</p>
        </div>
        <div
          class="form-group"
          :class="{ 'has-error': hasErrorsFor('message') }"
        >
          <label
            for="scheduledReport_message"
            class="control-label"
          >
            {{ messageLabel }}
          </label>
          <textarea
            id="scheduledReport_message"
            v-model="currentScheduledReport.message"
            class="form-control"
            :readonly="!customMessageAllowed"
            :maxlength="messageMaxlength"
            :placeholder="customMessageAllowed ? 'Enter a message' : defaultMessage"
          />
          <div
            v-for="error in errorsFor('message')"
            :key="error"
            class="help-block"
          >
            {{ error }}
          </div>
        </div>
        <div
          v-if="!customMessageAllowed"
          class="alert alert-warning"
        >
          <i class="fa fa-warning" />
          Custom messages are only enabled on paid plans
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-sm-6">
        <div class="form-group">
          <label
            for="scheduledReport_format"
            class="control-label"
          >
            File type
          </label>
          <AppSelect
            id="scheduledReport_format"
            v-model="currentScheduledReport.format"
            :options="formatOptions"
            :select2-options="commonSelect2Options"
            :disabled="formatSelectionDisabled"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-sm-6">
        <div
          class="form-group"
          :class="{ 'has-error': hasErrorsFor('name') }"
        >
          <label
            for="scheduledReport_name"
            class="control-label"
          >
            Name*
          </label>
          <AppInput
            id="scheduledReport_name"
            v-model="currentScheduledReport.name"
          />
          <div
            v-for="error in errorsFor('name')"
            :key="error"
            class="help-block"
          >
            {{ error }}
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-sm-6">
        <div class="form-group">
          <label
            for="scheduledReport_date_range"
            class="control-label"
          >
            Date range&nbsp;
            <AppTooltipIcon
              v-if="todayOnlyDateRange"
              message="This report can only run based on the day that it's sent"
              placement="auto top"
              :size="12"
            />
            <template v-else-if="!todayOnlyDateRange">*</template>
          </label>
          <AppSelect
            id="scheduledReport_date_range"
            v-model="currentScheduledReport.date_range"
            :options="dateRangeOptions"
            :select2-options="commonSelect2Options"
            :disabled="todayOnlyDateRange"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-sm-6">
        <div class="form-group">
          <label
            for="scheduledReport_interval"
            class="control-label"
          >
            Delivery frequency*
          </label>
          <AppSelect
            id="scheduledReport_interval"
            v-model="currentInterval"
            :options="SCHEDULED_REPORT_INTERVALS"
            :select2-options="commonSelect2Options"
          />
        </div>
      </div>
      <div class="col-xs-12 col-sm-6">
        <div class="form-group">
          <label
            for="scheduledReport_delivery_time"
            class="control-label"
          >
            Delivery time&nbsp;
            <AppTooltipIcon
              :message="`Time zone: ${timeZone}`"
              placement="auto top"
              :size="12"
            />
          </label>
          <AppTimeSelect
            id="scheduledReport_delivery_time"
            :value="currentScheduledReport.delivery_time"
            :minute-interval="30"
            @input="currentScheduledReport.delivery_time = $event"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <div class="form-group">
          <AppIntervalDayPicker
            v-if="hasInterval"
            v-model="currentScheduledReport.delivery_days"
            :mode="dayPickerMode"
            :start-week-on="startWeekOn"
            :multiple="isDailyInterval"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isBiweeklyInterval"
      class="row"
    >
      <div class="col-xs-12">
        <div class="form-group">
          <ReportScheduledReportBiweeklySendOn
            v-model="forceNextSendingAt"
            :delivery-days="currentScheduledReport.delivery_days"
            :delivery-time="currentScheduledReport.delivery_time"
            :next-sending-at="nextSendingAt"
            :time-zone="timeZoneId"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!deliveryEnabled && !limitReached"
      class="form-group well"
    >
      <i class="hi hi-info" />
      This report is paused. You can resume to restart deliveries.
    </div>
    <template #footer>
      <button
        id="scheduledReport_close_button"
        class="btn btn-default"
        :disabled="saving || savingPreview"
        data-testid="scheduled-report-close-button"
        @click="onClose"
      >
        {{ cancelButtonLabel }}
      </button>
      <button
        id="scheduledReport_preview_button"
        class="btn btn-default"
        :disabled="disableButtons"
        @click="onPreview"
      >
        {{ previewButtonLabel }}
      </button>
      <button
        id="scheduledReport_save_button"
        class="btn btn-primary"
        :disabled="disableButtons"
        @click="onSave"
      >
        {{ saveButtonLabel }}
      </button>
    </template>
  </AppDialog>
</template>

<script>
import { cloneDeep, isNil, isEmpty } from 'lodash-es';

import ReportScheduledReportBiweeklySendOn from './ReportScheduledReportBiweeklySendOn.vue';
import { OrganizationsResource } from '~/resources';

import {
  SCHEDULED_REPORT_DATE_RANGES,
  SCHEDULED_REPORT_DATE_RANGE_TODAY,
  SCHEDULED_REPORT_FORMATS,
  SCHEDULED_REPORT_INTERVALS,
  SCHEDULED_REPORT_INTERVAL_DAILY,
  SCHEDULED_REPORT_INTERVAL_WEEKLY,
  SCHEDULED_REPORT_INTERVAL_BIWEEKLY,
  SCHEDULED_REPORT_INTERVAL_MONTHLY,
  SCHEDULED_REPORT_WEEKLY_DATE_RANGES,
} from '~/app_constants';

const DEFAULT_DELIVERY_TIME = '2000-01-01T08:30:00Z';
const DEFAULT_DAILY_DELIVERY_DAYS = [1, 2, 3, 4, 5];
const DEFAULT_MONTHLY_DELIVERY_DAYS = [0]; // 1st of the month
const EMAIL_PATTERN = /.+@.+/;
const TODAY_ONLY_REPORTS = ['time_off_balances', 'team_invoices_aging', 'client_invoices_aging'];
const WEEKLY_ONLY_REPORTS = ['weekly'];
const DEFAULT_SCHEDULED_REPORT = {
  recipients: null,
  message: null,
  format: null,
  name: null,
  date_range: null,
  interval: null,
  delivery_days: [],
  delivery_time: DEFAULT_DELIVERY_TIME,
  force_next_sending_at: null,
};

export default {
  name: 'ReportScheduledReportDialog',
  components: {
    ReportScheduledReportBiweeklySendOn,
  },
  props: {
    customMessageAllowed: {
      type: Boolean,
      default: true,
    },
    defaultMessage: {},
    defaultSubject: {},
    errors: {},
    filterCount: {
      type: Number,
    },
    filtersDetailed: {
      type: Array,
      default: () => [],
    },
    formatOptionIds: {
      type: Array,
      default() {
        return SCHEDULED_REPORT_FORMATS.map(({ id }) => id);
      },
    },
    messageMaxlength: {
      type: Number,
      default: 400,
    },
    reportName: {
      type: String,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    savingPreview: {
      type: Boolean,
      default: false,
    },
    scheduledReport: {
      default() {
        return cloneDeep(DEFAULT_SCHEDULED_REPORT);
      },
    },
    limitReached: {
      type: Boolean,
      default: false,
    },
    scheduledReportsImprovements: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    startWeekOn: {
      type: Number,
      default: 0,
    },
    timeZone: {},
    timeZoneId: {},
    deliveryFrequency: {
      type: String,
      default: null,
    },
    organizationId: {
      type: Number,
      default: null,
    },
    hubstaffSgt: {
      type: Boolean,
      default: false,
    },
    cancelButtonLabel: {
      type: String,
      default: 'Cancel',
    },
  },
  data() {
    return {
      SCHEDULED_REPORT_DATE_RANGES,
      SCHEDULED_REPORT_INTERVALS,
      currentScheduledReport: null,
      commonSelect2Options: {
        width: '100%',
      },
      currentRecipients: null,
      showFilter: true,
    };
  },
  computed: {
    formatOptions() {
      return SCHEDULED_REPORT_FORMATS.filter(({ id }) => this.formatOptionIds.includes(id));
    },
    formatSelectionDisabled() {
      return this.formatOptions.length < 2;
    },
    todayOnlyDateRange() {
      return TODAY_ONLY_REPORTS.includes(this.reportName);
    },
    weekOnlyDateRange() {
      return WEEKLY_ONLY_REPORTS.includes(this.reportName);
    },
    dateRangeOptions() {
      if (this.weekOnlyDateRange) {
        return SCHEDULED_REPORT_WEEKLY_DATE_RANGES;
      }

      return SCHEDULED_REPORT_DATE_RANGES;
    },
    recipients: {
      get() {
        return this.currentRecipients;
      },
      set(recipientsString) {
        if (typeof recipientsString === 'string') {
          this.currentScheduledReport.recipients = recipientsString.replace(/\s/g, '').split(',');
        }

        this.currentRecipients = recipientsString;
      },
    },
    isNew() {
      return !this.currentScheduledReport.id;
    },
    showFiltersCount() {
      return !this.scheduledReportsImprovements && this.filterCount && this.filterCount > 0;
    },
    showFiltersDetailed() {
      return this.filtersDetailed.length > 0;
    },
    isValid() {
      const hasValidMessage = !this.customMessageAllowed || !isEmpty(this.currentScheduledReport.message);

      return !!(
        !isNil(this.currentScheduledReport.format) &&
        !isNil(this.currentScheduledReport.interval) &&
        !isNil(this.currentScheduledReport.date_range) &&
        this.currentScheduledReport.delivery_time &&
        Array.isArray(this.currentScheduledReport.delivery_days) &&
        this.currentScheduledReport.delivery_days.length > 0 &&
        Array.isArray(this.currentScheduledReport.recipients) &&
        this.currentScheduledReport.recipients.every((recipient) => EMAIL_PATTERN.test(recipient)) &&
        hasValidMessage
      );
    },
    isInvalid() {
      return !this.isValid;
    },
    isBiweeklyInterval() {
      return this.isInterval(SCHEDULED_REPORT_INTERVAL_BIWEEKLY);
    },
    isMonthlyInterval() {
      return this.isInterval(SCHEDULED_REPORT_INTERVAL_MONTHLY);
    },
    isDailyInterval() {
      return this.isInterval(SCHEDULED_REPORT_INTERVAL_DAILY);
    },
    dayPickerMode() {
      return this.isMonthlyInterval ? 'month' : 'week';
    },
    saveButtonLabel() {
      if (this.isNew) {
        return this.saving ? 'Saving...' : 'Save';
      }

      return this.saving ? 'Updating...' : 'Update';
    },
    previewButtonLabel() {
      return this.savingPreview ? 'Sending...' : 'Send preview';
    },
    messageLabel() {
      return `Message${this.customMessageAllowed ? '*' : ''}`;
    },
    disableButtons() {
      return this.isInvalid || this.saving || this.savingPreview;
    },
    hasInterval() {
      const type = typeof this.currentInterval;

      return type === 'number' || type === 'string';
    },
    currentInterval: {
      get() {
        return this.currentScheduledReport && this.currentScheduledReport.interval;
      },
      set(newValue) {
        let deliveryDays;

        switch (parseInt(newValue)) {
          case SCHEDULED_REPORT_INTERVAL_DAILY:
            deliveryDays = DEFAULT_DAILY_DELIVERY_DAYS;
            break;
          case SCHEDULED_REPORT_INTERVAL_WEEKLY:
          case SCHEDULED_REPORT_INTERVAL_BIWEEKLY: {
            const now = new Date();

            deliveryDays = [now.getDay()];
            break;
          }
          case SCHEDULED_REPORT_INTERVAL_MONTHLY:
            deliveryDays = DEFAULT_MONTHLY_DELIVERY_DAYS;
            break;
        }

        this.currentScheduledReport.interval = newValue;
        this.currentScheduledReport.delivery_days = deliveryDays;
      },
    },
    dateRangeLabel() {
      let dateRangeId = this.currentScheduledReport.date_range;

      if (!isNil(dateRangeId)) {
        dateRangeId = parseInt(dateRangeId);
        const dateRange = SCHEDULED_REPORT_DATE_RANGES.find((dateRange) => dateRange.id === dateRangeId);

        return dateRange && dateRange.text;
      }

      return null;
    },
    subject() {
      const dateRangeLabel = (this.dateRangeLabel || '...').toLowerCase();

      return this.defaultSubject.replace('__DATE_RANGE__', dateRangeLabel);
    },
    nextSendingAt() {
      let nextSendingAt = this.currentScheduledReport.next_sending_at;

      if (typeof nextSendingAt === 'string') {
        nextSendingAt = moment(nextSendingAt);
      }

      return nextSendingAt;
    },
    forceNextSendingAt: {
      get() {
        return this.currentScheduledReport.force_next_sending_at;
      },
      set(newValue) {
        this.currentScheduledReport.force_next_sending_at = moment(newValue);
      },
    },
    deliveryEnabled() {
      const enabled = this.scheduledReport.enabled;

      return typeof enabled === 'boolean' ? enabled : true;
    },
    upgradePlanLink() {
      return OrganizationsResource.plansLink({ id: this.organizationId });
    },
  },
  watch: {
    scheduledReport() {
      this.loadCurrentScheduledReport();
    },
    isBiweeklyInterval(value) {
      if (!value) {
        this.currentScheduledReport.force_next_sending_at = null;
      }
    },
  },
  mounted() {
    if (this.deliveryFrequency === 'weekly') {
      this.currentInterval = SCHEDULED_REPORT_INTERVAL_WEEKLY;
    }
  },
  created() {
    this.loadCurrentScheduledReport();
  },
  methods: {
    closeFilter() {
      this.showFilter = false;
    },
    isInterval(interval) {
      return !isNil(this.currentInterval) && parseInt(this.currentInterval) === interval;
    },
    loadCurrentScheduledReport() {
      this.currentScheduledReport = cloneDeep(this.scheduledReport);

      // Using a string because that's what select2 spits out
      if (!this.currentScheduledReport.format) {
        this.currentScheduledReport.format = this.formatOptions[0].id.toString();
      }

      if (Array.isArray(this.currentScheduledReport.recipients)) {
        this.currentRecipients = this.currentScheduledReport.recipients.join(', ');
      } else {
        this.currentRecipients = this.currentScheduledReport.recipients;
      }

      if (this.todayOnlyDateRange) {
        this.currentScheduledReport.date_range = SCHEDULED_REPORT_DATE_RANGE_TODAY.id;
      }
    },
    onClose(action = null) {
      this.$emit('close', action);
    },
    onSave() {
      this.$emit('save', this.currentScheduledReport);
    },
    onPreview() {
      this.$emit('preview', this.currentScheduledReport);
    },
    errorsFor(key) {
      if (this.errors) {
        return this.errors[key];
      }
    },
    hasErrorsFor(key) {
      const errorList = this.errorsFor(key);

      return Array.isArray(errorList) && errorList.length > 0;
    },
  },
};
</script>
